/* =====================================================================
   CUSTOM: Welcome hero — slider behaviour + "open the menu" buttons.
   ---------------------------------------------------------------------
   - Slides sit side by side in a flex track that is moved with translateX,
     so the transition is a single composited transform (smooth on mobile).
   - Autoplay every N seconds (data-autoplay, 0 = off), driven by a rAF loop
     that accumulates real elapsed time. It genuinely freezes while the hero
     has keyboard focus and while the tab is hidden — but NOT on hover, since
     a full-viewport hero is under the cursor by default. Any manual
     interaction restarts the timer.
   - The active dot draws that countdown as a ring around its number, so the
     time left on a slide is visible. It reads the same elapsed value as the
     autoplay, so the two can't drift apart.
   - A play/pause button sits before the numbers. Pausing holds the elapsed
     time, so play resumes the slide mid-ring rather than restarting it.
   - Drag/swipe: the track follows the finger and snaps to the nearest
     slide on release; a short flick counts as a full swipe.
   - Dots reflect and control the current slide.
   - prefers-reduced-motion: no autoplay, no animated transition.

   Paired with: sections/welcome-hero.liquid
   ===================================================================== */
(function () {
  var SWIPE_RATIO = 0.15; // of the hero width, to count as a swipe
  var SWIPE_MIN = 40; // px, so a flick on a narrow phone still counts

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initMenuButtons(section) {
    function openMenu(evt) {
      var mainMenu = document.querySelector('main-menu');
      if (!mainMenu) return;

      var isDesktop = window.theme && theme.mediaMatches && theme.mediaMatches.lg;

      if (!isDesktop) {
        var toggle = document.querySelector('.main-menu__toggle');
        if (toggle) toggle.click();
        return;
      }

      // Per-slide menu name, falling back to the section-wide one.
      var targetTitle = (evt.currentTarget.dataset.desktopMenuTitle
          || section.dataset.desktopMenuTitle || '').trim().toLowerCase();
      var details = null;

      mainMenu.querySelectorAll('.main-nav > li').forEach(function (li) {
        if (details) return;
        var link = li.querySelector('.main-nav__item--primary');
        var liDetails = li.querySelector('details');
        if (!link || !liDetails) return;
        if (!targetTitle || link.textContent.trim().toLowerCase().indexOf(targetTitle) !== -1) {
          details = liDetails;
        }
      });

      if (!details) details = mainMenu.querySelector('.main-nav > li details');

      if (details && !details.classList.contains('is-open')) {
        mainMenu.open(details);
        mainMenu.toggleOverlay(true);
      }
    }

    section.querySelectorAll('.js-wh-open-menu').forEach(function (btn) {
      btn.addEventListener('click', openMenu);
    });
  }

  function initSlider(section) {
    var track = section.querySelector('.wh__track');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('.wh__slide'));
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(section.querySelectorAll('.wh__dot'));
    var rings = dots.map(function (dot) {
      return dot.querySelector('.wh__dot-ring-fill');
    });
    var controls = section.querySelector('.wh__controls');
    var playBtn = section.querySelector('.wh__play');
    var interval = parseInt(section.dataset.autoplay, 10) || 0;
    var index = 0;
    var raf = null;
    var elapsed = 0; // ms counted against the current slide
    var lastFrame = 0;
    var paused = false; // transient: keyboard focus / hidden tab
    var playing = true; // deliberate: the play-pause button

    section.classList.add('wh--slider');

    function render(animate) {
      track.style.transition = animate && !reducedMotion()
          ? 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
          : 'none';
      track.style.transform = 'translate3d(' + (-100 * index) + '%, 0, 0)';

      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
        // Keep off-screen slides out of the tab order and the a11y tree.
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach(function (el) {
          if (i === index) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      // Empty every ring, so stepping back to an earlier slide doesn't show
      // the countdown it had left behind.
      rings.forEach(function (ring) {
        if (ring) ring.style.strokeDashoffset = '100';
      });
    }

    // 0 = empty, 1 = full circle. pathLength="100" on the <circle> normalises
    // the geometry, so the offset is just the percentage still to run.
    function drawProgress(ratio) {
      var ring = rings[index];
      if (!ring) return;
      var clamped = Math.max(0, Math.min(1, ratio));
      ring.style.strokeDashoffset = String(100 - clamped * 100);
    }

    function goTo(next, animate) {
      var count = slides.length;
      index = ((next % count) + count) % count; // wraps both ways
      render(animate !== false);
    }

    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = null;
      lastFrame = 0;
    }

    // Restarts the current slide's countdown from zero.
    function start() {
      stop();
      elapsed = 0;
      drawProgress(0);
      if (!interval || reducedMotion() || !playing) return;
      raf = requestAnimationFrame(frame);
    }

    // Pausing keeps `elapsed` where it is, so pressing play again resumes the
    // current slide rather than restarting it — the ring picks up mid-sweep.
    function setPlaying(next) {
      playing = next;
      if (playBtn) {
        playBtn.classList.toggle('is-playing', playing);
        playBtn.setAttribute('aria-label',
            playing ? playBtn.dataset.pauseLabel : playBtn.dataset.playLabel);
      }
      if (!playing) {
        stop();
        return;
      }
      lastFrame = 0; // don't bill the slide for the time spent paused
      if (!raf && interval && !reducedMotion()) raf = requestAnimationFrame(frame);
    }

    function frame(now) {
      raf = requestAnimationFrame(frame);

      var dt = lastFrame ? now - lastFrame : 0;
      lastFrame = now;

      // Focus inside a slide or a backgrounded tab freezes the countdown
      // instead of skipping an advance, so the ring always agrees with when
      // the slide will actually change.
      if (paused || document.hidden) return;

      // One long frame (tab restored, main thread blocked) must not jump the
      // countdown forward, so cap what any single frame can contribute.
      elapsed += Math.min(dt, 50);

      if (elapsed >= interval) {
        elapsed = 0;
        goTo(index + 1);
        return;
      }

      drawProgress(elapsed / interval);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
        start();
      });
    });

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        setPlaying(!playing);
      });
    }

    // Deliberately NO hover pause: the hero fills the viewport on load, so the
    // cursor sits over it by default and a hover pause would stop autoplay
    // more or less permanently on desktop.
    //
    // Focus inside a *slide* still pauses, so someone reading a slide's links
    // doesn't have it change under them. Focus on the pagination itself must
    // not, though: clicking a number or the play button focuses it, which
    // would otherwise freeze the countdown the instant you pressed play.
    section.addEventListener('focusin', function (evt) {
      paused = !(controls && controls.contains(evt.target));
    });
    section.addEventListener('focusout', function () { paused = false; });

    // Drag / swipe.
    var startX = 0;
    var startY = 0;
    var delta = 0;
    var dragging = false;
    var decided = false;

    section.addEventListener('pointerdown', function (evt) {
      if (evt.pointerType === 'mouse' && evt.button !== 0) return;
      dragging = true;
      decided = false;
      delta = 0;
      startX = evt.clientX;
      startY = evt.clientY;
    });

    section.addEventListener('pointermove', function (evt) {
      if (!dragging) return;
      var dx = evt.clientX - startX;
      var dy = evt.clientY - startY;

      // Let a vertical drag scroll the page instead of moving the slider.
      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragging = false;
          return;
        }
      }

      delta = dx;
      track.style.transition = 'none';
      track.style.transform =
          'translate3d(calc(' + (-100 * index) + '% + ' + delta + 'px), 0, 0)';
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      var threshold = Math.max(SWIPE_MIN, section.offsetWidth * SWIPE_RATIO);
      if (delta <= -threshold) {
        goTo(index + 1);
      } else if (delta >= threshold) {
        goTo(index - 1);
      } else {
        render(true); // snap back
      }
      delta = 0;
      start();
    }

    section.addEventListener('pointerup', endDrag);
    section.addEventListener('pointercancel', endDrag);
    section.addEventListener('pointerleave', endDrag);

    // A drag must not fire the button underneath the finger.
    section.addEventListener('click', function (evt) {
      if (Math.abs(delta) > 6) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);

    render(false);
    start();
  }

  function init(root) {
    (root || document).querySelectorAll('.cc-welcome-hero').forEach(function (section) {
      if (section.dataset.whReady === 'true') return;
      section.dataset.whReady = 'true';
      initMenuButtons(section);
      initSlider(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders the section on every change.
  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
